import type { CardDetailsFragment } from '@graphql/fragments/cards/core.graphql';
import { analytics } from '@modules/analytics';
import { mapGraphQLCardPropertiesToAnalyticsProperties } from '@modules/analytics/helpers';
import type { Maybe } from 'graphql/jsutils/Maybe';
import { useMemo } from 'react';

import { ShareSheet } from './ShareSheet';
import type { Card_Share_Methods } from '.cache/__types__';

interface ShareCardPopoverProps {
  card: Maybe<CardDetailsFragment>;
  show: boolean;
  closePopover: () => void;
  shareCardBtnNode: HTMLDivElement | HTMLButtonElement | undefined;
}

export const ShareCardPopover = ({
  card,
  show,
  closePopover,
  shareCardBtnNode,
}: ShareCardPopoverProps): JSX.Element => {
  const senderName = card?.properties?.first_name?.value?.trim();
  const senderOrgName = card?.profile?.organization;

  const qrCodeImage = useMemo(() => {
    // if show_image_in_qr_code card flag is true, and QR code url exists, return that url
    if (card?.flags?.show_image_in_qr_code && card?.images?.embedded_qr_code_image?.original_url) {
      return card?.images?.embedded_qr_code_image?.original_url;
    }

    return '';
  }, [card?.flags?.show_image_in_qr_code, card?.images?.embedded_qr_code_image]);

  if (!card) return <></>;

  const getAutofillMessage = (cardShareUrl: string) => {
    let greeting = `Hey there,`;
    if (senderName) {
      greeting = `Hey there, it's ${senderName}.`;
    }
    if (senderOrgName && senderName) {
      greeting = `Hey there, it's ${senderName} from ${senderOrgName}.`;
    }

    const emailSubject = senderName ? `${senderName}'s Blinq Card` : `Blinq Card inside!`;

    return {
      subject: emailSubject,
      body: `${greeting}%0D%0DClick this link to get my Blinq card: ${cardShareUrl}%0D%0DLooking forward to connecting further!`,
    };
  };

  const handleShareSuccess = (method: Card_Share_Methods) => {
    analytics.track({
      name: 'card_shared',
      properties: {
        card_share_method: method,
        ...mapGraphQLCardPropertiesToAnalyticsProperties(card),
      },
    });
  };

  return (
    <ShareSheet
      qrCodeSettings={{
        title: 'Your business card',
        image: qrCodeImage,
      }}
      onShareSuccess={handleShareSuccess}
      cardId={card.id}
      headerTitle="Share card"
      generatePrefilledMessage={getAutofillMessage}
      targetNode={shareCardBtnNode}
      isOpen={show}
      toggleOpenState={(isOpen) => {
        !isOpen && closePopover();
      }}
    />
  );
};
